/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import type { Product } from '@shared/types/products';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Edit2,
  Trash2,
  ExternalLink,
  Users,
  Zap,
  Link2,
  Calendar,
  Play,
  RefreshCw,
} from 'lucide-react';

export interface OverviewTabProps {
  product: Product;
  isActive: boolean;
  onEdit: (product: Product) => void;
  onDelete: () => void;
  onSetAsActive: () => void;
}

export function OverviewTab({ 
  product, 
  isActive, 
  onEdit, 
  onDelete,
  onSetAsActive 
}: OverviewTabProps) {
  const capabilities = product.capabilities ?? [];
  const users = product.users ?? [];
  const links = product.links ?? [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Main Panel */}
        <div className="md:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  {isActive && (
                    <Badge className="mb-2 bg-primary">Active Product</Badge>
                  )}
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-primary font-mono text-sm font-medium">
                      {product.productId}
                    </span>
                  </div>
                  <CardTitle className="text-2xl">{product.title}</CardTitle>
                  <CardDescription className="mt-2 text-base">
                    {product.description}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Owner */}
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Owner:</span>
                <span className="text-sm text-muted-foreground">{product.owner}</span>
              </div>

              {/* Capabilities */}
              {capabilities.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Capabilities</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {capabilities.map((capability, index) => (
                      <Badge key={index} variant="secondary">
                        {capability}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* User Roles */}
              {users.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">User Roles</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {users.map((user, index) => (
                      <Badge key={index} variant="outline">
                        {user}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Links */}
              {links.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Link2 className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Links</span>
                  </div>
                  <div className="flex flex-col gap-1">
                    {links.map((link, index) => (
                      <a
                        key={index}
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline flex items-center gap-1"
                      >
                        <ExternalLink className="h-3 w-3" />
                        {link}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Info Panel */}
        <div className="md:col-span-1 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Product Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {!isActive && product.repositoryUrl && (
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={onSetAsActive}
                >
                  <Play className="h-4 w-4 mr-2" />
                  Set as Active
                </Button>
              )}
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => onEdit(product)}
              >
                <Edit2 className="h-4 w-4 mr-2" />
                Edit Product
              </Button>
              <Button 
                variant="destructive" 
                className="w-full justify-start"
                onClick={onDelete}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Product
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Product Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Created</span>
                <span className="text-sm flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {new Date(product.createdAt).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Updated</span>
                <span className="text-sm flex items-center gap-1">
                  <RefreshCw className="h-3 w-3" />
                  {new Date(product.updatedAt).toLocaleDateString()}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
